<template>
  <bulk-update-wrapper
    :saving="addingJobsToBatch"
    :title="formTitle"
    hide-bottom-bar
    :has-changes="hasChanges"
    :show-save-button="false"
    @save="saveJobs"
    @dismiss="dismiss"
  >
    <vs-wrapper>
      <batch-edit-form-fields
        v-if="showEditBatchForm && selectedBatchId !== undefined && selectedBatchId !== null"
        :id="selectedBatchId"
        @batch-updated="routeToBatchPage"
      />
      <batch-draft-form
        v-else-if="showDraftForm"
        :batch-name="draftBatchName"
        :jobs-uuid-list="jobsUuidList"
        @batch-created-success="handleBatchCreatedSuccess"
        @batch-created-failure="handleBatchCreatedFailure"
      />
      <div v-else>
        <div class="mb-8">
          <batch-creation-form
            :initial-batch-number-value="currentSearchQuery"
            :add-divider="false"
            button-label="Create batch and add jobs"
            @empty-batch-number="handleEmptyBatchNumber"
            @batch-draft-requested="handleBatchDraftRequested"
          />
        </div>
        <batches-list ref="batchesListRef" :hub-id="hubId" @select="onSelect" />
      </div>
    </vs-wrapper>
    <template #error-snackbar>
      <vs-snackbar v-model="isSnackbarVisible" :type="snackbarType" sticky>
        {{ snackbarMessage ?? '' }}
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import { JobJson } from '@/models/types/job-json'
import { computed, ref } from 'vue'
import BulkUpdateWrapper from '@/components/common/BulkUpdateWrapper.vue'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { useAddJobsToInvoiceMutation } from '@/api/invoice'
import BatchesList from '@/components/batches/BatchesList.vue'
import { useRouter } from 'vue-router/composables'
import BatchCreationForm from '@/components/batches/BatchCreationForm.vue'
import { SnackType } from '@/components/vision/SnackBarType'
import BatchEditFormFields from '@/components/batches/BatchEditFormFields.vue'
import BatchDraftForm from '@/components/batches/BatchDraftForm.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { useJobSearchRouteContext } from '@/common/useJobSearchRouteContext'

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
}>()

const router = useRouter()
const { batchRouteName } = useJobSearchRouteContext()

const hubId = computed(() => (props.selectedJobs.length > 0 ? props.selectedJobs[0].hub_id : ''))

const {
  mutateAsync: addJobsToBatch,
  isLoading: addingJobsToBatch,
  error: addJobsToBatchError,
} = useAddJobsToInvoiceMutation()

const formTitle = computed<string>(() => (showEditBatchForm.value ? 'Edit Batch' : 'Add to Batch'))
const jobsUuidList = computed<string[]>(() => props.selectedJobs.map((j) => j.id))

const batchesListRef = ref()
const selectedBatchId = ref<string | null | undefined>()
const snackbarMessage = ref()
const hasChanges = computed(() => !!selectedBatchId.value)
const currentSearchQuery = ref('')
const showEditBatchForm = ref(false)
const isSnackbarVisible = ref(false)
const showDraftForm = ref(false)
const draftBatchName = ref('')
const snackbarType = ref(SnackType.ERROR)
const onSelect = async (newBatchId: string) => {
  selectedBatchId.value = newBatchId
  await saveJobs(true)
}

const routeToBatchPage = () => {
  const batchId = selectedBatchId.value
  if (!batchId) {
    return
  }

  const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
    Object.assign(job, { invoice_id: batchId }),
  )

  emits('updated', {
    message: 'Batch updated',
    updatedJobs,
  })

  router.push({
    name: batchRouteName.value,
    params: {
      batchId,
    },
  })
}

const saveJobs = async (routeToBatchOnSave: boolean) => {
  if (!selectedBatchId.value) {
    return
  }
  try {
    await addJobsToBatch({
      id: selectedBatchId.value,
      jobIds: props.selectedJobs.map((job) => job.id),
    })
    isSnackbarVisible.value = false

    if (routeToBatchOnSave) {
      routeToBatchPage()
    }
  } catch (err) {
    snackbarType.value = SnackType.ERROR
    isSnackbarVisible.value = true
    snackbarMessage.value = addJobsToBatchError.value
  }
}

const handleBatchCreatedSuccess = async (data: { batchId: string; batchNumber: string | null }) => {
  selectedBatchId.value = data.batchId

  if (showDraftForm.value) {
    showDraftForm.value = false
    showEditBatchForm.value = false
    routeToBatchPage()
  } else {
    showEditBatchForm.value = true
    await saveJobs(false) // allow users to edit the newly created batch
  }
}

const handleBatchCreatedFailure = async (error: unknown) => {
  snackbarType.value = SnackType.ERROR
  isSnackbarVisible.value = true
  snackbarMessage.value = error
}

const handleEmptyBatchNumber = () => {
  handleBatchCreatedFailure('Batch number cannot be empty')
}

const handleBatchDraftRequested = (data: { batchName: string }) => {
  draftBatchName.value = data.batchName
  showDraftForm.value = true
}

const dismiss = () => emits('dismiss')
</script>

<style scoped></style>
